import { useEffect, useState } from 'react';
import { CheckCircle2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import FlowShell from './FlowShell';
import {
  CARD_STYLE, TextField, SelectField, TextareaField, RadioField, CheckboxField,
  NextButton, PrimaryButton, formatServicePrice,
} from './FlowField';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const STEP_LABELS = ['Your Business', 'Situation', 'What You Need', 'Recommendation'];

const INDUSTRY_OPTIONS = [
  'Technology','Finance','Healthcare','Legal','Retail',
  'Education','Hospitality','Construction','Professional Services','Other',
];
const SIZE_OPTIONS = [
  { value: '1-10',    label: '1 to 10 people' },
  { value: '11-50',   label: '11 to 50 people' },
  { value: '51-200',  label: '51 to 200 people' },
  { value: '200+',    label: '200 plus' },
];
const CURRENT_MEASURES = [
  { value: 'comprehensive', label: 'We have a comprehensive security setup' },
  { value: 'some',          label: 'We have some basic measures' },
  { value: 'none',          label: 'We have nothing in place' },
  { value: 'not_sure',      label: 'We are not sure what we have' },
];
const INCIDENT_OPTIONS = [
  { value: 'yes',      label: 'Yes we have had an incident' },
  { value: 'no',       label: 'No' },
  { value: 'not_sure', label: 'Not sure or possibly' },
];
const CONCERN_OPTIONS = [
  { value: 'data_breach',   label: 'Data breach or theft' },
  { value: 'ransomware',    label: 'Ransomware attack' },
  { value: 'phishing',      label: 'Phishing and social engineering' },
  { value: 'cloud',         label: 'Cloud security' },
  { value: 'insider',       label: 'Employee or insider risk' },
  { value: 'compliance',    label: 'Compliance requirements' },
  { value: 'not_sure',      label: 'We are not sure — help us understand our risks' },
];
const LOOKING_FOR = [
  { value: 'monitoring',   label: 'Ongoing monitoring and protection' },
  { value: 'assessment',   label: 'A one-off security assessment' },
  { value: 'compliance',   label: 'Compliance certification help' },
  { value: 'cloud',        label: 'Cloud security review' },
  { value: 'not_sure',     label: 'Not sure — recommend something' },
];
const TIMELINE = [
  { value: 'urgent',   label: 'Urgent — we need this now' },
  { value: 'one_m',    label: 'Within 1 month' },
  { value: 'three_m',  label: 'Within 3 months' },
  { value: 'future',   label: 'Planning for the future' },
];

function buildRecommendation(form, pricing) {
  // Price-lookup helper by id → formatted string
  const by = {};
  for (const r of pricing) by[r.id] = r;
  const activeGuard = formatServicePrice(by['defend-active-guard-monitoring']);
  const vulnAssess  = formatServicePrice(by['defend-vulnerability-assessment']);
  const compliance  = formatServicePrice(by['defend-compliance-audit']);
  const cloud       = formatServicePrice(by['defend-cloud-security-review']);

  const small = form.company_size === '1-10';
  const noMeasures = form.current_security_measures === 'none';
  const hasIncident = form.security_incident_12m === 'yes';
  const notSureConcern = (form.primary_concerns || []).includes('not_sure');
  const wantsCompliance = (form.primary_concerns || []).includes('compliance') || form.looking_for === 'compliance';
  const wantsCloud = (form.primary_concerns || []).includes('cloud') || form.looking_for === 'cloud';
  const large = form.company_size === '200+' || form.company_size === '51-200';
  const hasExisting = form.current_security_measures === 'comprehensive';

  let title = 'Recommended next step';
  let body = '';
  let cta = activeGuard;

  if (small && noMeasures && notSureConcern) {
    title = 'Start with Acodia Watch monitoring.';
    body = 'Small businesses without existing security measures are statistically the most targeted. Attackers use automated tools that scan thousands of businesses every hour looking for easy entry points — and an unprotected small business is exactly what they are looking for. Acodia Watch provides continuous monitoring that catches threats before they become incidents. You do not need an enterprise budget to get properly protected.';
    cta = `Acodia Watch monitoring — starting from ${activeGuard}.`;
  } else if (hasIncident) {
    title = 'Start with a vulnerability assessment.';
    body = 'Having experienced a security incident significantly increases your risk of a follow-up attack. Bad actors often return to targets they know are vulnerable — or sell your details to others who will. We recommend starting with a full vulnerability assessment to understand your current exposure, then moving to ongoing monitoring to prevent recurrence.';
    cta = `Vulnerability assessment from ${vulnAssess}, then ${activeGuard} for Acodia Watch.`;
  } else if (wantsCompliance) {
    title = 'Start with a compliance gap analysis.';
    body = 'Achieving Cyber Essentials, ISO 27001, or GDPR compliance requires a structured approach and a clear understanding of where your current gaps are. We will assess your posture, identify what needs to change, and guide you through the certification process.';
    cta = `Compliance engagements start from ${compliance}.`;
  } else if (wantsCloud) {
    title = 'Start with a cloud security review.';
    body = 'Cloud misconfiguration is the single leading cause of data breaches in growing businesses — even those that have invested in other security measures. The problem is often invisible until it is too late. A targeted cloud security review across your AWS, Azure, GCP, or Microsoft 365 environment will identify your exposure within days.';
    cta = `Cloud security review starting from ${cloud}.`;
  } else if (large && hasExisting) {
    title = 'Add managed detection to your existing setup.';
    body = 'With an existing security setup the question is whether it is working as intended and whether your team has the capacity to respond effectively when something is detected. Managed Detection and Response adds expert eyes to your existing measures, ensuring nothing is missed.';
    cta = `Managed detection starting from ${activeGuard}.`;
  } else {
    title = 'Start with Acodia Watch monitoring.';
    body = 'Based on your answers, continuous monitoring is the highest-leverage first move. It gives you 24/7 eyes on your systems for a predictable monthly cost, and any findings flow directly to us for triage. We will discuss deeper assessments during your consultation.';
    cta = `Acodia Watch starting from ${activeGuard}.`;
  }

  return { title, body, cta };
}

export default function DefendFlow({ onExit }) {
  const [step, setStep] = useState(1);
  const [pricing, setPricing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', business_name: '', business_website: '',
    email: '', industry: '', company_size: '',
    current_security_measures: '', security_incident_12m: '',
    primary_concerns: [],
    looking_for: '', timeline: '', additional_details: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/service-pricing`);
        if (res.ok && !cancelled) setPricing((await res.json()).rows || []);
      } catch { /* fallback copy handles missing data */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const step1Ready = form.first_name && form.last_name && form.business_name
    && emailValid && form.industry && form.company_size;
  const step2Ready = form.current_security_measures && form.security_incident_12m
    && form.primary_concerns.length > 0;
  const step3Ready = form.looking_for && form.timeline;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email.trim().toLowerCase(),
          service: 'defend',
          source: 'pricing_questionnaire',
          interested_in: `Acodia Defend · ${form.business_name}`,
          answers: { ...form, email: undefined },
        }),
      });
      if (!res.ok) throw new Error(`submit failed: ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      toast.error('Submission failed', { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const rec = buildRecommendation(form, pricing);

  const renderStep1 = () => (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="First Name" value={form.first_name} onChange={(v) => set('first_name', v)} required testId="defend-first" />
        <TextField label="Last Name" value={form.last_name} onChange={(v) => set('last_name', v)} required testId="defend-last" />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Business Name" value={form.business_name} onChange={(v) => set('business_name', v)} required testId="defend-business-name" />
        <TextField label="Business Website" value={form.business_website} onChange={(v) => set('business_website', v)} placeholder="optional" testId="defend-website" />
      </div>
      <div className="mt-4">
        <TextField label="Email" type="email" value={form.email} onChange={(v) => set('email', v)} required testId="defend-email" />
        {form.email && !emailValid && <p className="font-body text-[11px] mt-1" style={{ color: 'var(--warn)' }}>Please enter a valid email address.</p>}
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField label="Industry" value={form.industry} onChange={(v) => set('industry', v)} options={INDUSTRY_OPTIONS} required testId="defend-industry" />
        <SelectField label="Company size" value={form.company_size} onChange={(v) => set('company_size', v)} options={SIZE_OPTIONS} required testId="defend-size" />
      </div>
      <NextButton disabled={!step1Ready} onClick={() => setStep(2)} testId="defend-step-1-next" />
    </>
  );

  const renderStep2 = () => (
    <>
      <RadioField label="Current security measures" value={form.current_security_measures} onChange={(v) => set('current_security_measures', v)} options={CURRENT_MEASURES} required testId="defend-measures" />
      <div className="mt-4">
        <RadioField label="Security incident in the last 12 months" value={form.security_incident_12m} onChange={(v) => set('security_incident_12m', v)} options={INCIDENT_OPTIONS} required testId="defend-incident" />
      </div>
      <div className="mt-4">
        <CheckboxField label="Primary concerns (select all that apply)" values={form.primary_concerns} onChange={(v) => set('primary_concerns', v)} options={CONCERN_OPTIONS} required testId="defend-concerns" />
      </div>
      <NextButton disabled={!step2Ready} onClick={() => setStep(3)} testId="defend-step-2-next" />
    </>
  );

  const renderStep3 = () => (
    <>
      <RadioField label="What are you looking for?" value={form.looking_for} onChange={(v) => set('looking_for', v)} options={LOOKING_FOR} required testId="defend-looking" />
      <div className="mt-4">
        <RadioField label="Timeline" value={form.timeline} onChange={(v) => set('timeline', v)} options={TIMELINE} required testId="defend-timeline" />
      </div>
      <div className="mt-4">
        <TextareaField
          label="Additional details"
          hint="Tell us about your business, any recent security concerns, compliance deadlines, or specific questions."
          value={form.additional_details}
          onChange={(v) => set('additional_details', v)}
          placeholder="Optional — the more context you share, the more useful your consultation will be."
          rows={5}
          testId="defend-details"
        />
      </div>
      <NextButton disabled={!step3Ready} onClick={() => setStep(4)} testId="defend-step-3-next" />
    </>
  );

  const renderStep4 = () => {
    if (submitted) {
      return (
        <div className="text-center max-w-xl mx-auto py-6" data-testid="defend-success">
          <div
            className="inline-flex items-center justify-center rounded-full mb-6"
            style={{ width: 64, height: 64, background: 'var(--ok-10)', border: '1px solid var(--ok)', boxShadow: '0 0 40px rgba(74,222,128,0.25)' }}
          >
            <CheckCircle2 size={32} style={{ color: 'var(--ok)' }} />
          </div>
          <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--white)' }}>
            Your details have been received.
          </h2>
          <p className="font-body text-sm" style={{ color: 'var(--tm)' }}>
            Expect a response within one business day. We will be in touch to confirm your consultation time.
          </p>
        </div>
      );
    }
    return (
      <>
        <div className="p-6 rounded-lg mb-6" style={{ ...CARD_STYLE, background: 'var(--a-glow)', border: '1px solid var(--a-border)' }} data-testid="defend-recommendation">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            recommendation
          </p>
          <h3 className="font-display text-xl font-semibold mt-2 mb-3" style={{ color: 'var(--white)' }}>{rec.title}</h3>
          <p className="font-body text-sm mb-4" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>{rec.body}</p>
          <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>{rec.cta}</p>
        </div>
        <PrimaryButton onClick={handleSubmit} disabled={submitting} testId="defend-step-4-submit">
          <Mail size={14} className="mr-2" />
          {submitting ? 'Sending…' : 'Book My Free Security Consultation'}
        </PrimaryButton>
        <a
          href="/watch"
          data-testid="defend-step-4-trial"
          className="mt-3 inline-flex items-center justify-center w-full font-body text-sm h-11 px-6 rounded-[3px]"
          style={{
            background: 'transparent',
            border: '1px solid var(--a)',
            color: 'var(--a)',
          }}
        >
          Or start your 7-day Acodia Watch trial now
        </a>
      </>
    );
  };

  return (
    <FlowShell
      kicker="acodia defend"
      title={submitted ? 'Thanks — we will be in touch.' : 'Let us find the right protection for your business.'}
      stepLabels={STEP_LABELS}
      step={step}
      onBack={() => setStep((s) => Math.max(1, s - 1))}
      onExit={onExit}
    >
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </FlowShell>
  );
}
